import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  FileText, TrendingUp, TrendingDown, Printer, Download,
  Award, AlertTriangle, Users, MessageSquare, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString('en-MY', { maximumFractionDigits: 0 }) : '—');
const fmtDec = (n, d = 2) => (Number.isFinite(n) ? n.toLocaleString('en-MY', { maximumFractionDigits: d, minimumFractionDigits: d }) : '—');
const pctFn = (a, b) => (b > 0 ? ((a - b) / b) * 100 : (a > 0 ? 100 : 0));

// Metric definitions — matches the KPI codes used in the Scorecard system
const METRICS = [
  { code: 'REV', label: 'Commission',    prefix: 'RM ', color: '#10B981' },
  { code: 'LEAD', label: 'Total Leads',  prefix: '',   color: '#3B82F6' },
  { code: 'CLS', label: 'Closed',        prefix: '',   color: '#F59E0B' },
  { code: 'SPD', label: 'Ad Spend',      prefix: 'RM ', color: '#EF4444', fromDerived: 'spend' },
  { code: 'ROI', label: 'ROI',           prefix: '',   suffix: '%', color: '#8B5CF6' },
  { code: 'DEP', label: 'Deposit',       prefix: '$ ', color: '#22D3EE', fromDerived: 'deposit_usd' },
  { code: 'ACA', label: 'Active Client', prefix: '',   color: '#EC4899' },
  { code: 'LOT', label: 'Traded Lot',    prefix: '',   color: '#F97316' },
];

function buildPeriodOptions() {
  const now = new Date();
  const opts = [];
  for (let i = -1; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    let label = d.toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });
    if (i === -1) label = `${label} (Next)`;
    else if (i === 0) label = `${label} (Current)`;
    opts.push({ value: v, label });
  }
  return opts;
}

// Small reusable stat card for the analytics grids
function MiniStat({ label, value, hint, color = '#9CA3AF', testId }) {
  return (
    <div
      className="rounded-lg border border-[#1f1f2b] bg-[#0f0f18] px-3 py-2.5"
      data-testid={testId}
    >
      <p className="text-[9px] uppercase tracking-widest text-[#6B7280] mb-1">{label}</p>
      <p className="text-base font-bold tabular-nums" style={{ color }}>{value}</p>
      {hint ? <p className="text-[10px] text-[#6B7280] mt-0.5">{hint}</p> : null}
    </div>
  );
}

// Auto-generated insights: pick top wins and concerns from metric rows
function computeInsights(metricRows, derived, targets) {
  const withDelta = metricRows.filter((m) => Number.isFinite(m.delta) && m.previous > 0);
  const wins = [...withDelta].sort((a, b) => b.delta - a.delta).slice(0, 3);
  const concerns = [];

  // Concern 1: negative delta metrics
  const negatives = [...withDelta].filter((m) => m.delta < -5).sort((a, b) => a.delta - b.delta).slice(0, 2);
  concerns.push(...negatives);

  // Concern 2: below-target metrics
  metricRows.forEach((m) => {
    if (m.target > 0) {
      const ach = m.current / m.target;
      if (ach < 0.7 && !concerns.find((c) => c.code === m.code)) {
        concerns.push({ ...m, _reason: 'target', _ach: ach });
      }
    }
  });

  // Concern 3: high cost metrics (CPS unusually high)
  if (derived?.cps > 20 && derived?.cps > 0) {
    concerns.push({ code: 'CPS', label: 'Cost per Subscriber', current: derived.cps, _reason: 'cost' });
  }

  return { wins: wins.slice(0, 3), concerns: concerns.slice(0, 3) };
}

/**
 * Dark-themed Channel Summary Report — mirrors the visual language of the
 * OverallPerformancePage report but scoped to a single channel. Uses REAL
 * data from /api/scorecard/channel-summary filtered to this IB.
 */
export default function ChannelSummaryReport({ open, onClose, ib }) {
  const currentYYYYMM = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(currentYYYYMM);
  const [current, setCurrent] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(false);

  const periodOptions = useMemo(buildPeriodOptions, []);

  useEffect(() => {
    if (!open || !ib?.id) return;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const h = { headers: { Authorization: `Bearer ${token}` } };
        const [py, pm] = period.split('-').map(Number);
        const prevD = new Date(py, pm - 2, 1);
        const prevPeriod = `${prevD.getFullYear()}-${String(prevD.getMonth() + 1).padStart(2, '0')}`;
        const [curRes, prevRes] = await Promise.all([
          axios.get(`${API}/scorecard/channel-summary?ib_id=${ib.id}&period=${period}`, h),
          axios.get(`${API}/scorecard/channel-summary?ib_id=${ib.id}&period=${prevPeriod}`, h),
        ]);
        setCurrent(curRes.data);
        setPrevious(prevRes.data);
      } catch (e) {
        toast.error('Gagal load data laporan');
        console.error(e);
      } finally { setLoading(false); }
    };
    load();
  }, [open, ib?.id, period]);

  const derived = current?.derived || {};

  const metricRows = useMemo(() => {
    const kpis = current?.kpis || {};
    const prevKpis = previous?.kpis || {};
    const targets = current?.targets || {};
    return METRICS.map((m) => {
      // Prefer derived source (e.g. SPD from `derived.spend`) when configured
      let cur = 0;
      let prev = 0;
      if (m.fromDerived) {
        cur = Number(derived?.[m.fromDerived] ?? 0);
        prev = Number(previous?.derived?.[m.fromDerived] ?? 0);
      } else if (m.code === 'ROI') {
        // Derive ROI = commission / spend * 100
        const spend = Number(derived?.spend || 0);
        const spendPrev = Number(previous?.derived?.spend || 0);
        cur = spend > 0 ? (Number(kpis.REV || 0) / spend) * 100 : 0;
        prev = spendPrev > 0 ? (Number(prevKpis.REV || 0) / spendPrev) * 100 : 0;
      } else {
        cur = Number(kpis[m.code] ?? 0);
        prev = Number(prevKpis[m.code] ?? 0);
      }
      const tgt = Number(targets[m.code] ?? 0);
      const delta = pctFn(cur, prev);
      return { ...m, current: cur, previous: prev, target: tgt, delta };
    });
  }, [current, previous, derived]);

  const insights = useMemo(
    () => computeInsights(metricRows, derived, current?.targets || {}),
    [metricRows, derived, current]
  );

  const periodLabel = useMemo(() => {
    const [y, mo] = period.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });
  }, [period]);

  const exportImage = async () => {
    const el = document.getElementById('channel-summary-report-content');
    if (!el) return;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(el, {
        backgroundColor: '#09090B', scale: 2, useCORS: true, logging: false,
        width: el.scrollWidth, height: el.scrollHeight,
        windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
        onclone: (doc) => {
          const t = doc.getElementById('channel-summary-report-content');
          if (t) { t.style.width = `${el.scrollWidth}px`; t.style.overflow = 'visible'; t.style.maxWidth = 'none'; }
        },
      });
      const link = document.createElement('a');
      link.download = `${ib?.name || 'Channel'}_report_${period}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Report exported');
    } catch (e) {
      toast.error(`Export failed: ${e?.message || 'unknown'}`);
    }
  };

  const tg = derived?.telegram || {};
  const yoy = derived?.yoy || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto bg-[#09090B] border-[#1f1f2b] text-white p-0" data-testid="channel-summary-report-dialog">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#0f0f18] to-[#1a1a25] px-6 py-4 border-b border-[#1f1f2b]">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#E63946] to-[#F97316] flex items-center justify-center">
                  <FileText size={22} />
                </div>
                <div>
                  <DialogTitle className="text-xl font-bold tracking-tight text-white">Channel Performance Report</DialogTitle>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">{ib?.name || 'Channel'} • {periodLabel}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-40 h-9 text-xs bg-[#0f0f18] border-[#2a2a35] text-white" data-testid="report-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f18] border-[#2a2a35] text-white">
                    {periodOptions.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => window.print()} className="border-[#2a2a35] bg-[#0f0f18] text-white hover:bg-[#1a1a25]" data-testid="report-print-btn">
                  <Printer size={13} className="mr-1.5" /> Print
                </Button>
                <Button size="sm" onClick={exportImage} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="report-export-img-btn">
                  <Download size={13} className="mr-1.5" /> Export PNG
                </Button>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Report Content */}
        <div id="channel-summary-report-content" className="p-6 space-y-5 bg-[#09090B]">
          {loading && (<div className="text-center py-8 text-sm text-[#9CA3AF]" data-testid="report-loading">Loading data...</div>)}
          {!loading && !current && (
            <div className="text-center py-8 text-sm text-amber-400">
              Tiada data untuk channel ini pada {periodLabel}. Pastikan targets & data dah di-set.
            </div>
          )}
          {!loading && current && (
            <>
              {/* Auto-Generated Wins & Concerns */}
              {(insights.wins.length > 0 || insights.concerns.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="report-insights">
                  <div className="rounded-xl border border-emerald-900/40 bg-emerald-950/20 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Award size={14} className="text-emerald-400" />
                      <h3 className="text-[11px] uppercase tracking-wider font-bold text-emerald-300">Top Wins</h3>
                    </div>
                    {insights.wins.length === 0 ? (
                      <p className="text-[11px] text-[#6B7280]">No standout wins vs previous month.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {insights.wins.map((w) => (
                          <li key={w.code} className="flex items-center justify-between text-[12px]">
                            <span className="text-white">{w.label}</span>
                            <span className="font-bold text-emerald-400 tabular-nums">+{w.delta.toFixed(1)}%</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="rounded-xl border border-red-900/40 bg-red-950/20 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle size={14} className="text-red-400" />
                      <h3 className="text-[11px] uppercase tracking-wider font-bold text-red-300">Top Concerns</h3>
                    </div>
                    {insights.concerns.length === 0 ? (
                      <p className="text-[11px] text-[#6B7280]">No red flags this period. Keep it up.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {insights.concerns.map((c, i) => (
                          <li key={c.code + i} className="flex items-center justify-between text-[12px]">
                            <span className="text-white">
                              {c.label}
                              {c._reason === 'target' && <span className="text-[#6B7280] ml-1">(target only {Math.round((c._ach || 0) * 100)}%)</span>}
                              {c._reason === 'cost' && <span className="text-[#6B7280] ml-1">(cost too high)</span>}
                            </span>
                            <span className="font-bold text-red-400 tabular-nums">
                              {Number.isFinite(c.delta) && c.delta < 0 ? `${c.delta.toFixed(1)}%` : c._reason === 'cost' ? `RM${fmtDec(c.current)}` : '↓'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {/* Key Metrics Grid — MoM comparison */}
              <div className="space-y-2">
                <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#9CA3AF]">Key Metrics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {metricRows.map((m) => {
                    const positive = m.delta >= 0;
                    const TrendIcon = positive ? TrendingUp : TrendingDown;
                    const trendColor = positive ? '#10B981' : '#EF4444';
                    return (
                      <div key={m.code} className="relative rounded-xl p-4 border border-[#1f1f2b] bg-gradient-to-br from-[#0f0f18] to-[#141420] overflow-hidden" data-testid={`report-kpi-${m.code}`}>
                        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${m.color}, transparent)` }} />
                        <p className="text-[10px] uppercase tracking-widest font-medium text-[#9CA3AF] mb-1">{m.label}</p>
                        <p className="text-2xl font-bold tabular-nums" style={{ color: m.color }}>
                          {m.prefix || ''}{fmt(m.current)}{m.suffix || ''}
                        </p>
                        <div className="flex items-center gap-1 mt-1 text-[11px] tabular-nums" style={{ color: trendColor }}>
                          <TrendIcon size={11} />
                          <span className="font-bold">{positive ? '+' : ''}{m.delta.toFixed(1)}%</span>
                          <span className="text-[#6B7280] font-normal">vs prev</span>
                        </div>
                        {m.target > 0 && (
                          <p className="text-[10px] text-[#6B7280] mt-1">
                            Target: {m.prefix || ''}{fmt(m.target)}{m.suffix || ''}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Cost Analytics */}
              <div className="space-y-2" data-testid="report-cost-analytics">
                <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#9CA3AF] flex items-center gap-2">
                  <DollarSign size={12} /> Cost Analytics & Efficiency
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MiniStat testId="stat-cps" label="Cost per Subscriber" value={derived.cps > 0 ? `RM${fmtDec(derived.cps)}` : '—'} hint="Spend ÷ subs" color="#F59E0B" />
                  <MiniStat testId="stat-cpl" label="Cost per Lead" value={derived.cpl > 0 ? `RM${fmtDec(derived.cpl)}` : '—'} hint="Spend ÷ leads" color="#3B82F6" />
                  <MiniStat testId="stat-cpc" label="Cost per Close" value={derived.cost_per_close > 0 ? `RM${fmtDec(derived.cost_per_close)}` : '—'} hint="Spend ÷ closed" color="#EF4444" />
                  <MiniStat testId="stat-cpca" label="Cost per Client" value={derived.cost_per_client > 0 ? `RM${fmtDec(derived.cost_per_client)}` : '—'} hint="Spend ÷ active" color="#8B5CF6" />
                  <MiniStat testId="stat-deposit-usd" label="Total Deposit" value={derived.deposit_usd > 0 ? `$${fmt(derived.deposit_usd)}` : '—'} hint={derived.deposit_myr > 0 ? `≈ RM${fmt(derived.deposit_myr)}` : ''} color="#22D3EE" />
                  <MiniStat testId="stat-deposit-per-client" label="Avg Deposit / Client" value={derived.deposit_per_client > 0 ? `$${fmtDec(derived.deposit_per_client)}` : '—'} hint="Deposit ÷ active" color="#10B981" />
                  <MiniStat testId="stat-vip-conv" label="VIP Conversion" value={derived.vip_conversion > 0 ? `${fmtDec(derived.vip_conversion, 1)}%` : '—'} hint="VIP subs ÷ total subs" color="#EC4899" />
                  <MiniStat testId="stat-cpl-usd" label="Comm / Lot" value={current?.kpis?.CPL > 0 ? `$${fmtDec(current.kpis.CPL)}` : '—'} hint="USD per lot" color="#F97316" />
                </div>
              </div>

              {/* Telegram Health */}
              {(tg.public?.followers > 0 || tg.vip?.followers > 0) && (
                <div className="space-y-2" data-testid="report-telegram-health">
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#9CA3AF] flex items-center gap-2">
                    <MessageSquare size={12} /> Telegram Channel Health
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {['public', 'vip'].map((k) => {
                      const t = tg[k] || {};
                      if (!(t.followers > 0)) return (
                        <div key={k} className="rounded-xl border border-[#1f1f2b] bg-[#0f0f18] p-4">
                          <p className="text-[11px] uppercase font-bold text-[#9CA3AF] mb-1">{k.toUpperCase()} Channel</p>
                          <p className="text-xs text-[#6B7280]">No Telegram data cached.</p>
                        </div>
                      );
                      return (
                        <div key={k} className="rounded-xl border border-[#1f1f2b] bg-[#0f0f18] p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[11px] uppercase font-bold text-[#9CA3AF]">{k.toUpperCase()} Channel</p>
                            <Users size={12} className="text-[#6B7280]" />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <MiniStat testId={`tg-${k}-followers`} label="Followers" value={fmt(t.followers)} color="#3B82F6" />
                            <MiniStat testId={`tg-${k}-vpp`} label="Views / Post" value={fmt(t.views_per_post)} color="#10B981" />
                            <MiniStat testId={`tg-${k}-eng`} label="Engagement" value={t.engagement_rate > 0 ? `${fmtDec(t.engagement_rate, 1)}%` : '—'} hint="Views ÷ subs" color="#F59E0B" />
                            <MiniStat testId={`tg-${k}-notif`} label="Notif Rate" value={t.notification_rate > 0 ? `${fmtDec(t.notification_rate, 1)}%` : '—'} hint="Enabled notifications" color="#EC4899" />
                          </div>
                          {t.post_count > 0 && (
                            <p className="text-[10px] text-[#6B7280] mt-2">{t.post_count} posts in cache</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Performance vs Target */}
              <div className="space-y-2">
                <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#9CA3AF]">Target Achievement</h3>
                <div className="rounded-xl border border-[#1f1f2b] bg-[#0f0f18] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#1f1f2b] bg-[#141420]">
                        <th className="text-left px-4 py-2 text-[#9CA3AF] font-medium">Metric</th>
                        <th className="text-right px-4 py-2 text-[#9CA3AF] font-medium">Actual</th>
                        <th className="text-right px-4 py-2 text-[#9CA3AF] font-medium">Target</th>
                        <th className="text-right px-4 py-2 text-[#9CA3AF] font-medium">Achievement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metricRows.filter((m) => m.target > 0).map((m) => {
                        const achievement = m.target > 0 ? (m.current / m.target) * 100 : 0;
                        const barColor = achievement >= 100 ? '#10B981' : achievement >= 75 ? '#F59E0B' : '#EF4444';
                        return (
                          <tr key={m.code} className="border-b border-[#1f1f2b]/50 last:border-b-0 hover:bg-[#141420]/50">
                            <td className="px-4 py-2.5 font-medium text-white">{m.label}</td>
                            <td className="text-right px-4 py-2.5 tabular-nums text-white">{m.prefix || ''}{fmt(m.current)}{m.suffix || ''}</td>
                            <td className="text-right px-4 py-2.5 tabular-nums text-[#9CA3AF]">{m.prefix || ''}{fmt(m.target)}{m.suffix || ''}</td>
                            <td className="text-right px-4 py-2.5 tabular-nums font-bold" style={{ color: barColor }}>
                              {Math.min(achievement, 100).toFixed(0)}%
                              <div className="w-24 h-1.5 rounded-full bg-[#1f1f2b] mt-1 ml-auto overflow-hidden">
                                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(achievement, 100)}%`, background: barColor }} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {metricRows.filter((m) => m.target > 0).length === 0 && (
                        <tr><td colSpan={4} className="text-center px-4 py-4 text-[#6B7280]">Belum ada targets di-set untuk period ini.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Year-over-Year Comparison */}
              {Object.keys(yoy).some((k) => yoy[k]?.prev > 0) ? (
                <div className="space-y-2" data-testid="report-yoy">
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#9CA3AF]">Year-over-Year</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {METRICS.filter((m) => yoy[m.code]?.prev > 0).map((m) => {
                      const y = yoy[m.code];
                      const cur = metricRows.find((x) => x.code === m.code)?.current || 0;
                      const positive = y.delta_pct >= 0;
                      const TrendIcon = positive ? TrendingUp : TrendingDown;
                      const tc = positive ? '#10B981' : '#EF4444';
                      return (
                        <div key={m.code} className="rounded-lg border border-[#1f1f2b] bg-[#0f0f18] p-3">
                          <p className="text-[10px] uppercase text-[#9CA3AF] mb-1">{m.label}</p>
                          <div className="flex items-baseline justify-between">
                            <p className="text-sm font-bold text-white tabular-nums">
                              {m.prefix || ''}{fmt(cur)}{m.suffix || ''}
                            </p>
                            <span className="flex items-center gap-0.5 text-[11px] font-bold tabular-nums" style={{ color: tc }}>
                              <TrendIcon size={10} />
                              {positive ? '+' : ''}{y.delta_pct.toFixed(1)}%
                            </span>
                          </div>
                          <p className="text-[10px] text-[#6B7280] mt-0.5">
                            vs {m.prefix || ''}{fmt(y.prev)}{m.suffix || ''} last year
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-2" data-testid="report-yoy-empty">
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#9CA3AF]">Year-over-Year</h3>
                  <div className="rounded-lg border border-[#1f1f2b] bg-[#0f0f18] p-4 text-center">
                    <p className="text-[11px] text-[#6B7280]">Tiada data tahun lepas untuk period ini. YoY akan tersedia bila data historikal wujud.</p>
                  </div>
                </div>
              )}

              {/* Footer note */}
              <div className="text-[10px] text-[#6B7280] pt-2 border-t border-[#1f1f2b]">
                Report generated {new Date().toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · Data pulled live from Scorecard system for {ib?.name || 'this channel'}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
